import { useMemo, useReducer, useRef } from "react";
import type { VoiceRecording } from "@/core/voice";
import type { Task } from "../types";
import type { TaskDraft } from "./taskDrawerForm";
import { CLOSED, TaskCreateDefaults, TaskDrawerState, taskDrawerReducer } from "./taskDrawerState";

/** What a list hands its TaskDrawer. Every action keeps its identity. */
export interface TaskDrawerController {
  state: TaskDrawerState;
  open: (task: Task) => void;
  create: (defaults: TaskCreateDefaults) => void;
  edit: () => void;
  changeDraft: (patch: Partial<TaskDraft>) => void;
  /** Create mode: the stopped recording the form holds, or null once dropped. */
  holdRecording: (recording: VoiceRecording | null) => void;
  /** View mode: the notes section sent the first note. */
  firstNoteTaken: () => void;
  cancel: () => void;
  /** Bind the session open now: pass the result as a save's `onSuccess`. */
  onSaved: () => (task: Task) => void;
  /** Same for a create's `onSuccess`. */
  onCreated: () => (task: Task) => void;
  synced: (task: Task) => void;
  confirmDelete: () => void;
  cancelDelete: () => void;
  close: () => void;
}

/** One drawer per list (DD-017): a row calls `open`, the list's add action
 * calls `create`, and the list renders `<TaskDrawer drawer={...} />`. */
export function useTaskDrawer(): TaskDrawerController {
  const [state, dispatch] = useReducer(taskDrawerReducer, CLOSED);
  const actions = useMemo(
    () => ({
      open: (task: Task) => dispatch({ type: "open", task }),
      create: (defaults: TaskCreateDefaults) => dispatch({ type: "create", defaults }),
      edit: () => dispatch({ type: "edit" }),
      changeDraft: (patch: Partial<TaskDraft>) => dispatch({ type: "draft", patch }),
      holdRecording: (recording: VoiceRecording | null) => dispatch({ type: "recording", recording }),
      firstNoteTaken: () => dispatch({ type: "firstNoteTaken" }),
      cancel: () => dispatch({ type: "cancel" }),
      synced: (task: Task) => dispatch({ type: "synced", task }),
      confirmDelete: () => dispatch({ type: "confirmDelete" }),
      cancelDelete: () => dispatch({ type: "cancelDelete" }),
      close: () => dispatch({ type: "close" }),
    }),
    []
  );
  // The session at mutate time is read from a ref, so the factories keep their identity.
  const sessionRef = useRef(state.session);
  sessionRef.current = state.session;
  const factories = useMemo(
    () => ({
      onSaved: () => {
        const session = sessionRef.current;
        return (task: Task) => dispatch({ type: "saved", task, session });
      },
      onCreated: () => {
        const session = sessionRef.current;
        return (task: Task) => dispatch({ type: "created", task, session });
      },
    }),
    []
  );
  return useMemo(() => ({ state, ...actions, ...factories }), [state, actions, factories]);
}
